import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { fetchFeatureAccess } from '@queries/featureAccess';
import { useQuery } from '@tanstack/react-query';

export const FEATURE_ACCESS_KEY = ['feature-access'] as const;

/**
 * The caller's limited-preview feature access, fetched once per session
 * (long staleTime — cohort changes are rare and a reload picks them up).
 * Defaults to `false` while loading or on error, so gated UI stays hidden
 * unless the backend positively confirms access. Skipped entirely on
 * builds where every preview-gated feature's flag is off (PPL), and each
 * answer is also masked by its own build flag, so a cohort row alone never
 * turns a feature on where its routes are not deployed.
 */
export const useFeatureAccess = () => {
  const getFeatureFlagState = useFeatureFlags();
  const pbqaFlagEnabled = getFeatureFlagState('PbqaEval') ?? false;
  const renewalSummaryFlagEnabled = getFeatureFlagState('RenewalSummary') ?? false;
  const flagEnabled = pbqaFlagEnabled || renewalSummaryFlagEnabled;

  const { data, isLoading } = useQuery({
    queryKey: FEATURE_ACCESS_KEY,
    queryFn: fetchFeatureAccess,
    enabled: flagEnabled,
    staleTime: 15 * 60 * 1000,
    retry: false,
  });

  return {
    pbqaEval: (pbqaFlagEnabled && data?.pbqa_eval) ?? false,
    pbqaReview: (pbqaFlagEnabled && data?.pbqa_review) ?? false,
    renewalSummary: (renewalSummaryFlagEnabled && data?.renewal_summary) ?? false,
    isLoading: flagEnabled && isLoading,
  };
};
